/**
 * Comment push digest. Emails readers a digest of comments "they may
 * have missed". The mailer is theoretical; the digest is computed and
 * dropped on the floor with a console line, which the desk calls
 * "shipping in shadow mode" and QA calls "shipping in ghost mode".
 */

import { threadFor } from "./store";

export interface DigestItem {
  story: string;
  comments: number;
  topAuthor: string;
  sample: string;
}

export function buildDigest(storyIds: string[]): DigestItem[] {
  const items: DigestItem[] = [];
  for (const id of storyIds) {
    const thread = threadFor(id);
    if (thread.length === 0) continue;
    const flat = flatten(thread);
    items.push({
      story: id,
      comments: flat.length,
      topAuthor: flat[0]?.author ?? "reader",
      sample: flat[0]?.body.slice(0, 96) ?? "",
    });
  }
  return items;
}

function flatten(comments: ReturnType<typeof threadFor>): Array<{ author: string; body: string }> {
  const out: Array<{ author: string; body: string }> = [];
  const walk = (list: typeof comments): void => {
    for (const c of list) {
      out.push({ author: c.author, body: c.body });
      if (c.replies) walk(c.replies);
    }
  };
  walk(comments);
  return out;
}

/** Digest cadence: weekly, except in universes with slower weeks. */
export const DIGEST_CADENCE: Record<string, string> = {
  "A-1": "weekly",
  "A-2": "weekly",
  "A-3": "weekly (paused: mascot litigation)",
  "B-11": "biweekly, opposite days",
  "C-04": "arrives before it is sent",
};

export function cadenceFor(universe: string): string {
  return DIGEST_CADENCE[universe] ?? "weekly";
}

export function shadowSend(items: DigestItem[]): number {
  // eslint-disable-next-line no-console
  console.info(`[wire] comment digest shadow-send: ${items.length} items, 0 inboxes touched`);
  return items.length;
}
